import * as THREE from 'three';
import { FillPattern } from '../types/floorplannerTypes';
import { canonicalizeFillPattern } from './fillPatterns';

// Keep the same world repeat for every texture so patterns scale consistently.
export const PATTERN_TILE_SIZE = 2.2;

const CANVAS_SIZE = 256;
const WHITE = '#ffffff';
const BLACK = '#000000';

let patternTextureCache: Map<FillPattern, THREE.CanvasTexture> | null = null;
let patternPreviewCache: Map<FillPattern, string> | null = null;

type RectSpec = { x: number; y: number; w: number; h: number };

const BASKETWEAVE_RECTS: RectSpec[] = [
  { x: 0.5, y: 0.0, w: 0.25, h: 0.75 },
  { x: 0.25, y: 0.0, w: 0.25, h: 0.25 },
  { x: 0.75, y: 0.0, w: 0.25, h: 0.25 },
  { x: 0.75, y: 0.25, w: 0.75, h: 0.25 },
  { x: 0.0, y: 0.5, w: 0.25, h: 0.75 },
  { x: 0.25, y: 0.5, w: 0.25, h: 0.25 },
  { x: 0.75, y: 0.5, w: 0.25, h: 0.25 },
  { x: 0.25, y: 0.75, w: 0.75, h: 0.25 },
];

function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function resetCanvas(ctx: CanvasRenderingContext2D): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.fillStyle = WHITE;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.strokeStyle = BLACK;
  ctx.fillStyle = BLACK;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
}

function drawDiagonalLines(
  ctx: CanvasRenderingContext2D,
  spacing: number,
  descending: boolean = false,
  overscan: number = CANVAS_SIZE,
): void {
  for (let start = -overscan; start <= CANVAS_SIZE + overscan; start += spacing) {
    ctx.beginPath();
    if (descending) {
      ctx.moveTo(start, CANVAS_SIZE);
      ctx.lineTo(start + CANVAS_SIZE, 0);
    } else {
      ctx.moveTo(start, 0);
      ctx.lineTo(start + CANVAS_SIZE, CANVAS_SIZE);
    }
    ctx.stroke();
  }
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = clamp01((value - edge0) / Math.max(edge1 - edge0, Number.EPSILON));
  return t * t * (3 - 2 * t);
}

function fract(value: number): number {
  return value - Math.floor(value);
}

function stripeDistance(coordinate: number, spacing: number): number {
  const wrapped = ((coordinate / spacing + 0.5) % 1 + 1) % 1;
  return Math.abs(wrapped - 0.5) * spacing;
}

function strokeMask(distanceValue: number, halfWidth: number, aa: number): number {
  return 1 - smoothstep(halfWidth - aa, halfWidth + aa, distanceValue);
}

function sampleRectBorderMask(
  sampleX: number,
  sampleY: number,
  rect: RectSpec,
  halfWidth: number,
  aa: number,
): number {
  const centerX = rect.x + rect.w * 0.5;
  const centerY = rect.y + rect.h * 0.5;
  const deltaX = Math.abs(sampleX - centerX) - rect.w * 0.5;
  const deltaY = Math.abs(sampleY - centerY) - rect.h * 0.5;
  const outside = Math.hypot(Math.max(deltaX, 0), Math.max(deltaY, 0));
  const inside = Math.min(Math.max(deltaX, deltaY), 0);
  return strokeMask(Math.abs(outside + inside), halfWidth, aa);
}

function sampleWrappedRectBorderMask(
  sampleX: number,
  sampleY: number,
  rect: RectSpec,
  halfWidth: number,
  aa: number,
): number {
  let mask = 0;

  for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
    for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
      mask = Math.max(
        mask,
        sampleRectBorderMask(
          sampleX,
          sampleY,
          { x: rect.x + offsetX, y: rect.y + offsetY, w: rect.w, h: rect.h },
          halfWidth,
          aa,
        ),
      );
    }
  }

  return mask;
}

function sampleBasketweaveMask(
  sampleX: number,
  sampleY: number,
  halfWidth: number,
  aa: number,
): number {
  let mask = 0;

  for (const rect of BASKETWEAVE_RECTS) {
    mask = Math.max(mask, sampleWrappedRectBorderMask(sampleX, sampleY, rect, halfWidth, aa));
  }

  return mask;
}

function hash21(x: number, y: number): number {
  return fract(Math.sin(x * 127.1 + y * 311.7) * 43758.5453123);
}

function renderProceduralPattern(
  ctx: CanvasRenderingContext2D,
  sampleMask: (u: number, v: number, aa: number) => number,
): void {
  resetCanvas(ctx);

  const imageData = ctx.createImageData(CANVAS_SIZE, CANVAS_SIZE);
  const { data } = imageData;
  const aa = 1 / CANVAS_SIZE;

  for (let y = 0; y < CANVAS_SIZE; y += 1) {
    for (let x = 0; x < CANVAS_SIZE; x += 1) {
      const u = (x + 0.5) / CANVAS_SIZE;
      const v = (y + 0.5) / CANVAS_SIZE;
      const mask = clamp01(sampleMask(u, v, aa));
      const color = 255 - Math.round(mask * 255);
      const index = (y * CANVAS_SIZE + x) * 4;

      data[index] = color;
      data[index + 1] = color;
      data[index + 2] = color;
      data[index + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

function ellipseRingMask(
  deltaX: number,
  deltaY: number,
  radiusX: number,
  radiusY: number,
  ringHalfWidth: number,
  aa: number,
): number {
  const ellipse = Math.hypot(deltaX / radiusX, deltaY / radiusY);
  const distanceValue = Math.abs(ellipse - 1) * Math.min(radiusX, radiusY);
  return strokeMask(distanceValue, ringHalfWidth, aa);
}

function sampleWoodPattern(u: number, v: number, aa: number): number {
  const px = u * 1.28;
  const py = v * 0.94;
  const gridX = px * 1.55;
  const gridY = py * 0.92;
  const baseCellX = Math.floor(gridX);
  const baseCellY = Math.floor(gridY);
  const localX = fract(gridX);
  const localY = fract(gridY);

  let axisOffset = 0;
  let knotMask = 0;

  for (let j = -1; j <= 1; j += 1) {
    for (let i = -1; i <= 1; i += 1) {
      const neighborX = baseCellX + i;
      const neighborY = baseCellY + j;
      const enabled = hash21(neighborX + 19.4, neighborY + 7.1) >= 0.68 ? 1 : 0;
      if (!enabled) {
        continue;
      }

      const pointX = hash21(neighborX + 3.7, neighborY + 5.9);
      const pointY = hash21(neighborX + 11.2, neighborY + 13.6);
      const centerX = i + (0.22 + (0.78 - 0.22) * pointX);
      const centerY = j + (0.2 + (0.8 - 0.2) * pointY);
      const deltaX = localX - centerX;
      const deltaY = localY - centerY;
      const ellipse = Math.hypot(deltaX / 0.085, deltaY / 0.235);
      const influence = Math.exp(-ellipse * ellipse * 1.7);

      axisOffset += Math.sign(deltaX) * 0.05 * influence;

      const outerRing = ellipseRingMask(deltaX, deltaY, 0.085, 0.235, 0.0034, aa);
      const innerRing = ellipseRingMask(deltaX, deltaY, 0.05, 0.145, 0.0028, aa) * 0.78;
      knotMask = Math.max(knotMask, Math.max(outerRing, innerRing));
    }
  }

  const axis =
    px +
    axisOffset +
    0.06 * Math.sin(py * Math.PI * 1.12) +
    0.018 * Math.sin(py * Math.PI * 4.2 + 0.8) +
    0.01 * Math.sin((py + px * 0.22) * Math.PI * 9 + 1.9);

  const primary = strokeMask(stripeDistance(axis, 0.165), 0.0048, aa);
  const secondary = strokeMask(
    stripeDistance(axis + 0.038 + 0.01 * Math.sin(py * Math.PI * 1.7 + 0.6), 0.33),
    0.0034,
    aa,
  );
  const tertiary = strokeMask(
    stripeDistance(axis - 0.026 + 0.007 * Math.sin(py * Math.PI * 2.4 + 2.1), 0.495),
    0.0026,
    aa,
  );

  return Math.max(Math.max(primary, Math.max(secondary * 0.92, tertiary * 0.72)), knotMask * 0.96);
}

function woodContourField(u: number, v: number): number {
  const px = u * 1.45 * Math.PI;
  const py = v * 1.45 * Math.PI;
  const warpedX = px + 0.42 * Math.sin(py * 1.08 + 0.34 * Math.cos(px * 0.72 + 0.45));
  const warpedY = py + 0.36 * Math.sin(px * 0.94 - 0.38 * Math.cos(py * 0.78 + 1.1));

  return (
    0.62 * Math.sin(warpedX) +
    0.52 * Math.sin(warpedY + 0.24 * Math.cos(warpedX * 0.82)) +
    0.24 * Math.sin(warpedX * 0.74 + warpedY * 0.98 + 0.38)
  );
}

function sampleAngledParquetPattern(
  u: number,
  v: number,
  aa: number,
  parquetScale: number,
  plankStep: number,
): number {
  const invSqrt2 = Math.SQRT1_2;
  const seamHalfWidth = 0.0075;
  const aaScaled = aa * parquetScale;
  const axisA = (u * invSqrt2 + v * invSqrt2) * parquetScale;
  const axisB = (-u * invSqrt2 + v * invSqrt2) * parquetScale;
  const cellA = Math.floor(axisA);
  const cellB = Math.floor(axisB);
  const localA = fract(axisA);
  const localB = fract(axisB);
  const parity = ((cellA + cellB) % 2 + 2) % 2;

  if (parity === 0) {
    return Math.max(
      strokeMask(stripeDistance(localB, plankStep), seamHalfWidth, aaScaled),
      strokeMask(Math.min(localA, 1 - localA), seamHalfWidth, aaScaled),
    );
  }

  return Math.max(
    strokeMask(stripeDistance(localA, plankStep), seamHalfWidth, aaScaled),
    strokeMask(Math.min(localB, 1 - localB), seamHalfWidth, aaScaled),
  );
}

function sampleHerringbonePattern(u: number, v: number, aa: number): number {
  const gridX = u * 3.8;
  const gridY = v * 1.82;
  const columnRise = 0.48;
  const plankPitch = columnRise * Math.SQRT1_2 * 0.9;
  const column = Math.floor(gridX);
  const localX = fract(gridX);
  const parity = (((column % 2) + 2) % 2);
  const direction = parity === 0 ? 1 : -1;
  const phase = parity * (columnRise % plankPitch);
  const diagonalAxis = gridY - direction * columnRise * localX - phase;
  const aaScaled = aa * 3.8;
  const seamHalfWidth = 0.0075;

  return Math.max(
    strokeMask(Math.min(localX, 1 - localX), seamHalfWidth, aaScaled),
    strokeMask(stripeDistance(diagonalAxis, plankPitch), seamHalfWidth * 0.88, aaScaled),
  );
}

function sampleBasketweavePattern(u: number, v: number, aa: number): number {
  const invSqrt2 = Math.SQRT1_2;
  // Must satisfy invSqrt2 * weaveScale = integer for seamless texture wrapping
  // after the 45° rotation. 2 * √2 yields shift of 2 per UV unit.
  const weaveScale = 2 * Math.SQRT2;
  const seamHalfWidth = 0.015;
  const localX = fract((u * invSqrt2 + v * invSqrt2) * weaveScale);
  const localY = fract((-u * invSqrt2 + v * invSqrt2) * weaveScale);

  return sampleBasketweaveMask(
    localX,
    localY,
    seamHalfWidth,
    aa * weaveScale,
  );
}

function sampleChevronPattern(u: number, v: number, aa: number): number {
  return sampleAngledParquetPattern(u, v, aa, 4.4, 0.25);
}

function signedDistanceToRotatedPlank(
  sampleX: number,
  sampleY: number,
  centerX: number,
  centerY: number,
  cos: number,
  sin: number,
  halfWidth: number,
  halfLength: number,
): number {
  const localX = sampleX - centerX;
  const localY = sampleY - centerY;
  const along = localX * cos + localY * sin;
  const across = -localX * sin + localY * cos;
  const deltaAlong = Math.abs(along) - halfLength;
  const deltaAcross = Math.abs(across) - halfWidth;
  const outside = Math.hypot(Math.max(deltaAlong, 0), Math.max(deltaAcross, 0));
  const inside = Math.min(Math.max(deltaAlong, deltaAcross), 0);

  return outside + inside;
}

function strokeWrappedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  repeatX: number,
  repeatY: number,
): void {
  const xOffsets = x + width > repeatX ? [0, -repeatX] : [0];
  const yOffsets = y + height > repeatY ? [0, -repeatY] : [0];

  xOffsets.forEach((xOffset) => {
    yOffsets.forEach((yOffset) => {
      ctx.strokeRect(x + xOffset, y + yOffset, width, height);
    });
  });
}

function drawOffsetColumnPaversPattern(
  ctx: CanvasRenderingContext2D,
  brickWidth: number,
  brickHeight: number,
  columnOffset: number = brickHeight * 0.5,
): void {
  for (let col = -2; col < Math.ceil(CANVAS_SIZE / brickWidth) + 2; col += 1) {
    const baseX = col * brickWidth;
    const offsetY = col % 2 === 0 ? 0 : columnOffset;

    for (let row = -2; row < Math.ceil(CANVAS_SIZE / brickHeight) + 3; row += 1) {
      ctx.strokeRect(baseX, row * brickHeight - offsetY, brickWidth, brickHeight);
    }
  }
}

function drawShiftedStaggeredPaversPattern(ctx: CanvasRenderingContext2D, unit: number): void {
  const repeat = unit * 5;
  const largePavers: RectSpec[] = [
    { x: 0, y: 0, w: 2, h: 2 },
    { x: 2, y: 1, w: 2, h: 2 },
    { x: 4, y: 2, w: 2, h: 2 },
    { x: 1, y: 3, w: 2, h: 2 },
    { x: 3, y: 4, w: 2, h: 2 },
  ];
  const smallPavers: RectSpec[] = [
    { x: 2, y: 0, w: 1, h: 1 },
    { x: 4, y: 1, w: 1, h: 1 },
    { x: 1, y: 2, w: 1, h: 1 },
    { x: 3, y: 3, w: 1, h: 1 },
    { x: 0, y: 4, w: 1, h: 1 },
  ];

  for (let row = -2; row < Math.ceil(CANVAS_SIZE / repeat) + 2; row += 1) {
    const baseY = row * repeat;

    for (let col = -2; col < Math.ceil(CANVAS_SIZE / repeat) + 2; col += 1) {
      const baseX = col * repeat;

      largePavers.forEach((rect) => {
        ctx.strokeRect(
          baseX + rect.x * unit,
          baseY + rect.y * unit,
          rect.w * unit,
          rect.h * unit,
        );
      });

      smallPavers.forEach((rect) => {
        ctx.strokeRect(
          baseX + rect.x * unit,
          baseY + rect.y * unit,
          rect.w * unit,
          rect.h * unit,
        );
      });
    }
  }
}

function drawCluster(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  angle: number,
  spacing: number,
  length: number,
  count: number,
): void {
  const sin = Math.sin(angle);
  const cos = Math.cos(angle);

  for (let index = 0; index < count; index += 1) {
    const offset = (index - (count - 1) / 2) * spacing;
    const x1 = centerX + -cos * length * 0.5 + -sin * offset;
    const y1 = centerY + -sin * length * 0.5 + cos * offset;
    const x2 = centerX + cos * length * 0.5 + -sin * offset;
    const y2 = centerY + sin * length * 0.5 + cos * offset;

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
}

function generateWoodPattern(ctx: CanvasRenderingContext2D): void {
  renderProceduralPattern(ctx, sampleWoodPattern);
}

function generateWoodContourPattern(ctx: CanvasRenderingContext2D): void {
  renderProceduralPattern(ctx, (u, v, aa) => {
    const field = woodContourField(u, v);
    const primary = strokeMask(stripeDistance(field, 0.22), 0.0048, aa);
    const secondary = strokeMask(
      stripeDistance(field + 0.065 * Math.sin(u * Math.PI * 1.3 + v * Math.PI * 0.7), 0.44),
      0.0034,
      aa,
    );

    return Math.max(primary, secondary * 0.72);
  });
}

function generateGrassPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 2;

  const spacingX = 40;
  const spacingY = 34;

  for (let row = -1; row < Math.ceil(CANVAS_SIZE / spacingY) + 2; row += 1) {
    const y = row * spacingY + 24;
    const xOffset = row % 2 === 0 ? 0 : spacingX / 2;

    for (let col = -1; col < Math.ceil(CANVAS_SIZE / spacingX) + 2; col += 1) {
      const x = col * spacingX + xOffset + 20;

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x - 2, y - 10, x - 8, y - 16);
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x, y - 12, x, y - 18);
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x + 2, y - 10, x + 8, y - 16);
      ctx.stroke();
    }
  }
}

function generateStipplePattern(ctx: CanvasRenderingContext2D, seed: number, count: number, radiusRange: [number, number]): void {
  const random = createRandom(seed);
  for (let index = 0; index < count; index += 1) {
    const x = random() * CANVAS_SIZE;
    const y = random() * CANVAS_SIZE;
    const radius = radiusRange[0] + random() * (radiusRange[1] - radiusRange[0]);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
}

function generateClusteredStipplePattern(
  ctx: CanvasRenderingContext2D,
  seed: number,
  clusterCount: number,
  dotsPerClusterRange: [number, number],
  spreadRange: [number, number],
  radiusRange: [number, number],
): void {
  const random = createRandom(seed);
  for (let clusterIndex = 0; clusterIndex < clusterCount; clusterIndex += 1) {
    const centerX = random() * CANVAS_SIZE;
    const centerY = random() * CANVAS_SIZE;
    const dotCount =
      dotsPerClusterRange[0] +
      Math.floor(random() * (dotsPerClusterRange[1] - dotsPerClusterRange[0] + 1));

    for (let dotIndex = 0; dotIndex < dotCount; dotIndex += 1) {
      const angle = random() * Math.PI * 2;
      const distance =
        spreadRange[0] + random() * (spreadRange[1] - spreadRange[0]);
      const radius =
        radiusRange[0] + random() * (radiusRange[1] - radiusRange[0]);

      ctx.beginPath();
      ctx.arc(
        centerX + Math.cos(angle) * distance,
        centerY + Math.sin(angle) * distance,
        radius,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
  }
}

function generateSandPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  generateStipplePattern(ctx, 3003, 5600, [0.26, 0.52]);
  generateStipplePattern(ctx, 3004, 2600, [0.14, 0.3]);
}

function generateCementPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 2.05;

  const random = createRandom(4004);
  for (let index = 0; index < 420; index += 1) {
    const x = random() * CANVAS_SIZE;
    const y = random() * CANVAS_SIZE;
    const angle = random() * Math.PI * 2;
    const length = 3.5 + random() * 4;

    ctx.beginPath();
    ctx.moveTo(x - Math.cos(angle) * length * 0.5, y - Math.sin(angle) * length * 0.5);
    ctx.lineTo(x + Math.cos(angle) * length * 0.5, y + Math.sin(angle) * length * 0.5);
    if (random() > 0.28) {
      ctx.moveTo(x - Math.cos(angle + Math.PI / 2) * length * 0.22, y - Math.sin(angle + Math.PI / 2) * length * 0.22);
      ctx.lineTo(x + Math.cos(angle + Math.PI / 2) * length * 0.22, y + Math.sin(angle + Math.PI / 2) * length * 0.22);
    }
    ctx.stroke();
  }

  generateStipplePattern(ctx, 4005, 640, [0.42, 0.82]);
}

function generateConcretePattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 1.9;

  const random = createRandom(5005);
  for (let index = 0; index < 30; index += 1) {
    const x = random() * CANVAS_SIZE;
    const y = random() * CANVAS_SIZE;
    const size = 4 + random() * 4;
    const angle = random() * Math.PI * 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const vertices = [
      { x: 0, y: -size },
      { x: size * 0.82, y: size * 0.5 },
      { x: -size * 0.82, y: size * 0.5 },
    ].map((vertex) => ({
      x: x + vertex.x * cos - vertex.y * sin,
      y: y + vertex.x * sin + vertex.y * cos,
    }));

    ctx.beginPath();
    ctx.moveTo(vertices[0].x, vertices[0].y);
    ctx.lineTo(vertices[1].x, vertices[1].y);
    ctx.lineTo(vertices[2].x, vertices[2].y);
    ctx.closePath();
    ctx.stroke();
  }

  generateStipplePattern(ctx, 5006, 1450, [1.35, 2.45]);
  generateStipplePattern(ctx, 5008, 680, [0.95, 1.65]);
  generateClusteredStipplePattern(ctx, 5007, 118, [4, 9], [1.0, 6.6], [1.05, 1.85]);
}

function generateVoronoiStonePattern(ctx: CanvasRenderingContext2D, seed: number, pointCount: number, threshold: number): void {
  resetCanvas(ctx);

  const random = createRandom(seed);
  const points = Array.from({ length: pointCount }, () => ({
    x: random() * CANVAS_SIZE,
    y: random() * CANVAS_SIZE,
  }));

  const imageData = ctx.createImageData(CANVAS_SIZE, CANVAS_SIZE);
  const { data } = imageData;

  for (let y = 0; y < CANVAS_SIZE; y += 1) {
    for (let x = 0; x < CANVAS_SIZE; x += 1) {
      let nearest = Number.POSITIVE_INFINITY;
      let secondNearest = Number.POSITIVE_INFINITY;

      for (let index = 0; index < points.length; index += 1) {
        const point = points[index];
        let dx = Math.abs(x - point.x);
        let dy = Math.abs(y - point.y);

        dx = Math.min(dx, CANVAS_SIZE - dx);
        dy = Math.min(dy, CANVAS_SIZE - dy);

        const distance = dx * dx + dy * dy;
        if (distance < nearest) {
          secondNearest = nearest;
          nearest = distance;
        } else if (distance < secondNearest) {
          secondNearest = distance;
        }
      }

      const gap = Math.sqrt(secondNearest) - Math.sqrt(nearest);
      const isEdge = gap < threshold;
      const pixel = (y * CANVAS_SIZE + x) * 4;
      const color = isEdge ? 0 : 255;

      data[pixel] = color;
      data[pixel + 1] = color;
      data[pixel + 2] = color;
      data[pixel + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

function generateStoneLargePattern(ctx: CanvasRenderingContext2D): void {
  generateVoronoiStonePattern(ctx, 6006, 18, 0.8);
}

function generateStoneSmallPattern(ctx: CanvasRenderingContext2D): void {
  generateVoronoiStonePattern(ctx, 7007, 14, 1.4);
}

function generateBrickPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 1.15;
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';

  const brickWidth = 36;
  const brickHeight = 12;

  for (let y = 0; y <= CANVAS_SIZE; y += brickHeight) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(CANVAS_SIZE, y);
    ctx.stroke();
  }

  const rows = Math.ceil(CANVAS_SIZE / brickHeight) + 1;
  for (let row = -1; row < rows; row += 1) {
    const y = row * brickHeight;
    const offset = row % 2 === 0 ? 0 : brickWidth / 2;
    for (let x = -brickWidth; x <= CANVAS_SIZE + brickWidth; x += brickWidth) {
      ctx.beginPath();
      ctx.moveTo(x + offset, y);
      ctx.lineTo(x + offset, y + brickHeight);
      ctx.stroke();
    }
  }
}

function generateEarthPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 1.35;
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';

  const cell = 40;
  const inset = 7;
  const start = 7;
  const spacing = 6;
  const lineLength = cell - inset * 2;

  for (let row = -1; row < Math.ceil(CANVAS_SIZE / cell) + 2; row += 1) {
    for (let col = -1; col < Math.ceil(CANVAS_SIZE / cell) + 2; col += 1) {
      const x = col * cell;
      const y = row * cell;

      if ((row + col) % 2 === 0) {
        ctx.beginPath();
        for (let i = 0; i < 4; i += 1) {
          const lineX = x + start + i * spacing;
          ctx.moveTo(lineX, y + inset);
          ctx.lineTo(lineX, y + inset + lineLength);
        }
        ctx.stroke();
      } else {
        ctx.beginPath();
        for (let i = 0; i < 4; i += 1) {
          const lineY = y + start + i * spacing;
          ctx.moveTo(x + inset, lineY);
          ctx.lineTo(x + inset + lineLength, lineY);
        }
        ctx.stroke();
      }
    }
  }
}

function generateConcreteBlockPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 0.8;
  drawDiagonalLines(ctx, 18, false);
  drawDiagonalLines(ctx, 18, true);
}

function generateSteelPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.strokeStyle = '#9a9a9a';
  ctx.lineWidth = 1.15;
  drawDiagonalLines(ctx, 10, false);
  ctx.strokeStyle = '#d6d6d6';
  ctx.lineWidth = 0.42;
  drawDiagonalLines(ctx, 10, false);
}

function generateGlassPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.strokeStyle = '#8e8e8e';
  ctx.lineWidth = 0.5;

  const patches = [
    { x: 66, y: 46 },
    { x: 156, y: 150 },
    { x: 214, y: 76 },
  ];

  patches.forEach((patch) => {
    drawCluster(ctx, patch.x, patch.y, Math.PI / 4, 10, 66, 2);
  });
}

function generateRccPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 0.8;
  drawDiagonalLines(ctx, 16, false);
}

function generatePaversOctagonPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 0.8;

  const cell = 64;
  const cut = 14;

  for (let row = -1; row < Math.ceil(CANVAS_SIZE / cell) + 2; row += 1) {
    for (let col = -1; col < Math.ceil(CANVAS_SIZE / cell) + 2; col += 1) {
      const x = col * cell;
      const y = row * cell;

      ctx.beginPath();
      ctx.moveTo(x + cut, y);
      ctx.lineTo(x + cell - cut, y);
      ctx.lineTo(x + cell, y + cut);
      ctx.lineTo(x + cell, y + cell - cut);
      ctx.lineTo(x + cell - cut, y + cell);
      ctx.lineTo(x + cut, y + cell);
      ctx.lineTo(x, y + cell - cut);
      ctx.lineTo(x, y + cut);
      ctx.closePath();
      ctx.stroke();
    }
  }
}

function generatePavementGridPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 0.8;
  drawOffsetColumnPaversPattern(ctx, 32, 64);
}

function generateBasketweavePattern(ctx: CanvasRenderingContext2D): void {
  renderProceduralPattern(ctx, sampleBasketweavePattern);
}

function generateRunningPaversPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  const repeatX = ctx.canvas.width;
  const repeatY = ctx.canvas.height;
  const unitWidth = repeatX / 4;
  const unitHeight = repeatY / 4;
  const lineScale = repeatX / CANVAS_SIZE;
  ctx.lineWidth = 6.0 * lineScale;

  const rects: RectSpec[] = [
    { x: 0, y: 0, w: 2, h: 1 },
    { x: 0, y: 1, w: 1, h: 2 },
    { x: 1, y: 1, w: 2, h: 1 },
    { x: 3, y: 0, w: 1, h: 2 },
    { x: 2, y: 3, w: 1, h: 2 },
    { x: 1, y: 2, w: 1, h: 2 },
    { x: 2, y: 2, w: 2, h: 1 },
    { x: 3, y: 3, w: 2, h: 1 },
  ];

  rects.forEach((rect) => {
    strokeWrappedRect(
      ctx,
      rect.x * unitWidth,
      rect.y * unitHeight,
      rect.w * unitWidth,
      rect.h * unitHeight,
      repeatX,
      repeatY,
    );
  });
}

function generateStaggeredPaversPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 1.1;
  drawShiftedStaggeredPaversPattern(ctx, 18);
}

function generateChevronLinesPattern(ctx: CanvasRenderingContext2D): void {
  resetCanvas(ctx);
  ctx.lineWidth = 0.8;

  const halfWidth = 38;
  const pitch = 76;

  for (let centerX = -pitch; centerX <= CANVAS_SIZE + pitch; centerX += pitch) {
    for (let y = -pitch; y <= CANVAS_SIZE + pitch; y += pitch) {
      ctx.beginPath();
      ctx.moveTo(centerX, y);
      ctx.lineTo(centerX + halfWidth, y + halfWidth);
      ctx.moveTo(centerX, y);
      ctx.lineTo(centerX - halfWidth, y + halfWidth);
      ctx.stroke();
    }
  }
}

function generateChevronPattern(ctx: CanvasRenderingContext2D): void {
  renderProceduralPattern(ctx, sampleChevronPattern);
}

function generateHerringbonePattern(ctx: CanvasRenderingContext2D): void {
  renderProceduralPattern(ctx, sampleHerringbonePattern);
}

function generateHerringbone2Pattern(ctx: CanvasRenderingContext2D): void {
  const canvasWidth = ctx.canvas.width;
  const canvasHeight = ctx.canvas.height;
  const tileScale = canvasWidth / CANVAS_SIZE;
  const columnPitch = 64 * tileScale;
  const rowStep = 32 * tileScale;
  const phaseOffset = rowStep / 2;
  const plankWidth = 18 * tileScale;
  const plankLength = 80 * tileScale;
  const fillBleed = 0;
  const startX = columnPitch / 2;
  const startY = rowStep / 2;
  const columnPeriod = Math.max(1, Math.round(canvasWidth / columnPitch));
  const rowPeriod = Math.max(1, Math.round(canvasHeight / rowStep));
  const halfWidth = (plankWidth + fillBleed * 2) / 2;
  const halfLength = (plankLength + fillBleed * 2) / 2;
  const labels = new Int32Array(canvasWidth * canvasHeight);

  const wrapIndex = (value: number, size: number): number => ((value % size) + size) % size;
  const getOwner = (sampleX: number, sampleY: number): number => {
    const baseColumn = Math.floor((sampleX - startX) / columnPitch);
    let bestDistance = Number.POSITIVE_INFINITY;
    let bestOwner = 0;

    for (let columnOffset = -2; columnOffset <= 2; columnOffset += 1) {
      const col = baseColumn + columnOffset;
      const positiveSlope = wrapIndex(col, 2) === 0;
      const angle = positiveSlope ? Math.PI / 4 : -Math.PI / 4;
      const sin = Math.sin(angle);
      const cos = Math.cos(angle);
      const phase = positiveSlope ? 0 : phaseOffset;
      const centerX = startX + col * columnPitch;
      const baseRow = Math.floor((sampleY - startY - phase) / rowStep);

      for (let rowOffset = -3; rowOffset <= 3; rowOffset += 1) {
        const row = baseRow + rowOffset;
        const centerY = startY + row * rowStep + phase;
        const distance = signedDistanceToRotatedPlank(
          sampleX,
          sampleY,
          centerX,
          centerY,
          cos,
          sin,
          halfWidth,
          halfLength,
        );

        if (distance < bestDistance) {
          bestDistance = distance;
          bestOwner = wrapIndex(col, columnPeriod) * rowPeriod + wrapIndex(row, rowPeriod);
        }
      }
    }

    return bestOwner;
  };

  for (let y = 0; y < canvasHeight; y += 1) {
    const sampleY = y + 0.5;

    for (let x = 0; x < canvasWidth; x += 1) {
      labels[y * canvasWidth + x] = getOwner(x + 0.5, sampleY);
    }
  }

  const boundaryMask = new Uint8Array(canvasWidth * canvasHeight);
  const imageData = ctx.createImageData(canvasWidth, canvasHeight);
  const { data } = imageData;

  for (let y = 0; y < canvasHeight; y += 1) {
    for (let x = 0; x < canvasWidth; x += 1) {
      const index = y * canvasWidth + x;
      const owner = labels[index];
      const left = labels[y * canvasWidth + ((x - 1 + canvasWidth) % canvasWidth)];
      const right = labels[y * canvasWidth + ((x + 1) % canvasWidth)];
      const up = labels[((y - 1 + canvasHeight) % canvasHeight) * canvasWidth + x];
      const down = labels[((y + 1) % canvasHeight) * canvasWidth + x];
      boundaryMask[index] = left !== owner || right !== owner || up !== owner || down !== owner ? 1 : 0;
    }
  }

  for (let y = 0; y < canvasHeight; y += 1) {
    for (let x = 0; x < canvasWidth; x += 1) {
      const pixel = (y * canvasWidth + x) * 4;
      const color = boundaryMask[y * canvasWidth + x] === 1 ? 0 : 255;
      data[pixel] = color;
      data[pixel + 1] = color;
      data[pixel + 2] = color;
      data[pixel + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

function generatePatternCanvas(pattern: FillPattern): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const resolvedPattern = canonicalizeFillPattern(pattern);
  const canvasSize =
    resolvedPattern === 'herringbone2'
      ? CANVAS_SIZE * 4
      : resolvedPattern === 'runningPavers'
      ? CANVAS_SIZE * 2
      : CANVAS_SIZE;
  canvas.width = canvasSize;
  canvas.height = canvasSize;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error(`Failed to get 2D context for pattern ${pattern}`);
  }

  switch (resolvedPattern) {
    case 'wood':
      generateWoodPattern(ctx);
      break;
    case 'woodContour':
      generateWoodContourPattern(ctx);
      break;
    case 'grass':
      generateGrassPattern(ctx);
      break;
    case 'sand':
      generateSandPattern(ctx);
      break;
    case 'cement':
      generateCementPattern(ctx);
      break;
    case 'concrete':
      generateConcretePattern(ctx);
      break;
    case 'stoneLarge':
      generateStoneLargePattern(ctx);
      break;
    case 'stoneSmall':
      generateStoneSmallPattern(ctx);
      break;
    case 'brick':
      generateBrickPattern(ctx);
      break;
    case 'earth':
      generateEarthPattern(ctx);
      break;
    case 'concreteBlock':
      generateConcreteBlockPattern(ctx);
      break;
    case 'steel':
      generateSteelPattern(ctx);
      break;
    case 'glass':
      generateGlassPattern(ctx);
      break;
    case 'rcc':
      generateRccPattern(ctx);
      break;
    case 'paversOctagon':
      generatePaversOctagonPattern(ctx);
      break;
    case 'pavementGrid':
      generatePavementGridPattern(ctx);
      break;
    case 'basketweave':
      generateBasketweavePattern(ctx);
      break;
    case 'runningPavers':
      generateRunningPaversPattern(ctx);
      break;
    case 'staggeredPavers':
      generateStaggeredPaversPattern(ctx);
      break;
    case 'herringbone':
      generateHerringbonePattern(ctx);
      break;
    case 'herringbone2':
      generateHerringbone2Pattern(ctx);
      break;
    case 'chevron':
      generateChevronPattern(ctx);
      break;
    case 'chevronLines':
      generateChevronLinesPattern(ctx);
      break;
    default:
      throw new Error(`Unknown pattern type: ${pattern}`);
  }

  return canvas;
}

function initializeCache(): void {
  if (!patternTextureCache) {
    patternTextureCache = new Map();
    patternPreviewCache = new Map();
  }
}

export function getPatternTexture(pattern: FillPattern): THREE.CanvasTexture | null {
  initializeCache();

  const resolvedPattern = canonicalizeFillPattern(pattern) ?? pattern;
  if (patternTextureCache!.has(resolvedPattern)) {
    return patternTextureCache!.get(resolvedPattern)!;
  }

  const canvas = generatePatternCanvas(resolvedPattern);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;

  patternTextureCache!.set(resolvedPattern, texture);
  return texture;
}

export function getPatternPreviewDataURL(pattern: FillPattern): string {
  initializeCache();

  const resolvedPattern = canonicalizeFillPattern(pattern) ?? pattern;
  if (patternPreviewCache!.has(resolvedPattern)) {
    return patternPreviewCache!.get(resolvedPattern)!;
  }

  const canvas = generatePatternCanvas(resolvedPattern);
  const dataUrl = canvas.toDataURL('image/png');

  patternPreviewCache!.set(resolvedPattern, dataUrl);
  return dataUrl;
}

export function disposeAllPatternTextures(): void {
  if (patternTextureCache) {
    patternTextureCache.forEach((texture) => {
      texture.dispose();
    });
    patternTextureCache.clear();
    patternTextureCache = null;
  }

  if (patternPreviewCache) {
    patternPreviewCache.clear();
    patternPreviewCache = null;
  }
}
